import React, { useEffect, useState, useRef, useMemo } from "react";
import { LiveProvider, LiveError, LivePreview } from "react-live";
import * as LucideIcons from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadFontsFromData, getFontFamily } from "../../utils/fontLoader";
import PreviewError from "./PreviewError";
import FloatingChatInput from "./FloatingChatInput";
import { detectComponentInfo, formatMessageWithContext } from "../../utils/componentDetector";
import { injectDataAttributes } from "../../utils/visualEditor/templateEnhancer";
import { validateTemplate } from "../../utils/templateValidator";

import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { 
  Pie, 
  Bar, 
  Line, 
  Doughnut, 
  Radar, 
  PolarArea,
  Bubble,
  Scatter
} from 'react-chartjs-2';

// Register ALL Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

function CollateralPreview({ 
  template, 
  data, 
  withContainer = false,
  onErrorReport,
  onQuickEdit 
}) {
      

  const [previewError, setPreviewError] = useState(null);
  const errorRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [floatingChat, setFloatingChat] = useState(null);

  // Load fonts when data changes
  useEffect(() => {
    if (data) {
      loadFontsFromData(data);
    }
  }, [data]);

  // Inject data-path attributes after render for visual editing
  useEffect(() => {
    if (!previewContainerRef.current || !data) return;

    // Small delay to ensure DOM is fully rendered
    const timeout = setTimeout(() => {
      try {
        injectDataAttributes(previewContainerRef.current, data);
        console.log('✅ Injected data-path attributes for visual editing');
      } catch (error) {
        console.error('Error injecting data attributes:', error);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [data, template]);

  // Monitor LiveError content changes to capture error messages
  useEffect(() => {
    if (!errorRef.current) return;

    const observer = new MutationObserver(() => {
      const errorText = errorRef.current?.textContent?.trim();
      if (errorText) {
        setPreviewError(errorText);
      } else {
        setPreviewError(null);
      }
    });

    observer.observe(errorRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Initial check
    const errorText = errorRef.current?.textContent?.trim();
    if (errorText) {
      setPreviewError(errorText);
    } else {
      setPreviewError(null);
    }

    return () => observer.disconnect();
  }, [template, data]);

  // Handle double-click events on preview
  useEffect(() => {
    if (!onQuickEdit) return;

    const handleDoubleClick = (event) => {
      // Don't trigger on floating chat input or error messages
      if (
        event.target.closest('.floating-chat-input') ||
        event.target.closest('[class*="PreviewError"]') ||
        event.target.closest('button')
      ) {
        return;
      }

      // Find the actual element that was clicked (not the wrapper)
      const clickedElement = event.target;
      
      // Detect component information
      const componentInfo = detectComponentInfo(clickedElement);
      
      // Format default message with context
      const defaultMessage = formatMessageWithContext(componentInfo, '');
      
      // Show floating chat at click position
      setFloatingChat({
        x: event.clientX,
        y: event.clientY,
        componentInfo,
        defaultMessage,
      });
    };

    // Attach listener to the container
    const container = previewContainerRef.current;
    if (container) {
      container.addEventListener('dblclick', handleDoubleClick, true);
      return () => {
        container.removeEventListener('dblclick', handleDoubleClick, true);
      };
    }
  }, [onQuickEdit]);

  // Handle floating chat message send
  const handleFloatingChatSend = (message) => {
    if (floatingChat && onQuickEdit) {
      // Message already includes context from FloatingChatInput
      onQuickEdit(message);
    }
    setFloatingChat(null);
  };

  // Handle floating chat close
  const handleFloatingChatClose = () => {
    setFloatingChat(null);
  };

  // Extract component name from template
  const componentMatch = template.match(/function\s+(\w+)/);
  const componentName = componentMatch ? componentMatch[1] : 'DynamicComponent';

  // Prepare the code for react-live
  const [cleanTemplate, setCleanTemplate] = useState('');
  


  // useEffect(() => {
  //   if (!template) {
  //     setCleanTemplate('');
  //     return;
  //   }

  //   try {
  //     // 🔍 Validate template in development mode
  //     if (process.env.NODE_ENV === 'development') {
  //       const validation = validateTemplate(template);
  //       if (!validation.isValid) {
  //         console.group('⚠️ Template Validation Issues');
  //         console.warn('Errors:', validation.errors);
  //         console.warn('Warnings:', validation.warnings);
  //         console.log('These issues will be auto-fixed by the template cleaner.');
  //         console.groupEnd();
  //       }
  //     }

  //     let cleanedTemplate = template
  //     .replace(/import\s+.*?from\s+['"].*?['"];?\n?/g, '')
  //     .replace(/export default\s+/g, "");
    
  //   // ========================================
  //   // 🔧 FIX COMMON LLM-GENERATED ERRORS
  //   // ========================================
    
  //   // 1️⃣ Remove problematic window/global object destructuring
  //   // LLMs sometimes try to get React libraries from window/global
  //   cleanedTemplate = cleanedTemplate
  //     .replace(/const\s+\{\s*motion\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '')
  //     .replace(/const\s+\{\s*motion\s*\}\s*=\s*global\s*\|\|\s*\{\};?\n?/g, '')
  //     .replace(/const\s+\{\s*AnimatePresence\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '')
  //     .replace(/const\s+motion\s*=\s*window\.motion;?\n?/g, '')
  //     .replace(/const\s+\{\s*React\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '');
    
  //   // 2️⃣ Fix malformed className with template literals
  //   // Fix cases where template literals might have issues with quotes
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /(className=\{\`[\s\S]*?)"(\s*\})/g,
  //     '$1`$2'
  //   );
    
  //   // Fix className with mixed quotes and template literals
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /className="([^"]*\{[^}]+\}[^"]*)"/g,
  //     (match, content) => {
  //       const fixedContent = content.replace(/\{([^}]+)\}/g, '${$1}');
  //       return `className={\`${fixedContent}\`}`;
  //     }
  //   );
    
  //   // Fix unclosed template literals in className
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /className=\{\`([^`]*?)(?=\`\}|\s*\/>|\s*>)/g,
  //     (match, content) => {
  //       // If we find an unclosed template literal, try to fix it
  //       if (!match.includes('`}')) {
  //         return `className={\`${content}\`}`;
  //       }
  //       return match;
  //     }
  //   );
    
  //   // Fix template literals that might have been broken by JSON encoding
  //   // Sometimes backticks get escaped or replaced incorrectly
  //   cleanedTemplate = cleanedTemplate.replace(/\\`/g, '`'); // Unescape backticks
  //   cleanedTemplate = cleanedTemplate.replace(/className=\{\s*"([^"]*)"\s*\}/g, (match, content) => {
  //     // Convert string className to template literal if it has template-like syntax
  //     if (content.includes('${') || content.includes('`')) {
  //       return `className={\`${content.replace(/\$\{/g, '${')}\`}`;
  //     }
  //     return match;
  //   });
  
  //   // 3️⃣ Fix common Framer Motion prop errors
  //   // Fix missing commas in motion prop objects
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /(initial|animate|variants|transition|whileHover|whileInView|whileTap|exit)=\{\{([^}]+)\}\}/g,
  //     (match, prop, content) => {
  //       try {
  //         // Ensure proper comma separation in objects
  //         let fixed = content
  //           .replace(/(\w+):\s*([^,}\s]+)\s+(\w+):/g, '$1: $2, $3:') // Add missing commas
  //           .replace(/,\s*}/g, '}') // Remove trailing commas
  //           .replace(/}\s*{/g, '}, {'); // Fix adjacent objects
          
  //         // Fix invalid repeat values (must be non-negative or Infinity)
  //         // Replace negative repeat values with Infinity
  //         fixed = fixed.replace(/repeat:\s*-\d+/g, 'repeat: Infinity');
          
  //         // Fix repeat: -Infinity or similar issues
  //         fixed = fixed.replace(/repeat:\s*-Infinity/g, 'repeat: Infinity');
          
  //         // Ensure repeat is either a positive number or Infinity
  //         fixed = fixed.replace(/repeat:\s*([a-zA-Z_$][a-zA-Z0-9_$]*)/g, (m, varName) => {
  //           // If it's a variable that might be undefined/negative, default to Infinity
  //           if (varName !== 'Infinity') {
  //             return 'repeat: Infinity';
  //           }
  //           return m;
  //         });
          
  //         return `${prop}={{${fixed}}}`;
  //       } catch (e) {
  //         console.warn(`Failed to fix motion prop ${prop}:`, e);
  //         return match;
  //       }
  //     }
  //   );
    
  //   // 3.5️⃣ Fix ALL transition objects to ensure valid repeat values
  //   // This catches cases where transition is defined separately
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /transition=\{\s*\{([^}]+)\}\s*\}/g,
  //     (match, content) => {
  //       try {
  //         let fixed = content;
          
  //         // If it has a repeat property, fix it
  //         if (/repeat\s*:/.test(fixed)) {
  //           // Fix all invalid repeat patterns
  //           fixed = fixed.replace(/repeat:\s*-\d+/g, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*-Infinity/g, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*null/gi, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*undefined/gi, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*NaN/gi, 'repeat: Infinity');
            
  //           // Fix repeat with expressions that might evaluate to negative
  //           // e.g., repeat: someVar, repeat: data.speed, etc.
  //           fixed = fixed.replace(/repeat:\s*([a-zA-Z_$][a-zA-Z0-9_$\.]*)/g, (m, varName) => {
  //             if (varName !== 'Infinity') {
  //               return 'repeat: Infinity';
  //             }
  //             return m;
  //           });
  //         }
          
  //         return `transition={{ ${fixed} }}`;
  //       } catch (e) {
  //         console.warn('Failed to fix transition repeat value:', e);
  //         return match;
  //       }
  //     }
  //   );
    
  //   // 3.6️⃣ Extra safety: Fix animate props with repeat
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /animate=\{\s*\{([^}]+)\}\s*\}/g,
  //     (match, content) => {
  //       try {
  //         if (/repeat\s*:/.test(content)) {
  //           let fixed = content;
  //           fixed = fixed.replace(/repeat:\s*-\d+/g, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*-Infinity/g, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*null/gi, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*undefined/gi, 'repeat: Infinity');
  //           fixed = fixed.replace(/repeat:\s*([a-zA-Z_$][a-zA-Z0-9_$\.]*)/g, (m, varName) => {
  //             if (varName !== 'Infinity') {
  //               return 'repeat: Infinity';
  //             }
  //             return m;
  //           });
  //           return `animate={{ ${fixed} }}`;
  //         }
  //         return match;
  //       } catch (e) {
  //         return match;
  //       }
  //     }
  //   );
    
  //   // 4️⃣ Fix undefined or incorrect icon references
  //   // Replace common icon mistakes and non-existent icons
  //   cleanedTemplate = cleanedTemplate
  //     .replace(/LucideIcons\.undefined/g, 'LucideIcons.Info')
  //     .replace(/undefined\s+className/g, 'LucideIcons.Info className')
  //     // Fix common incorrect icon names that don't exist in lucide-react
  //     .replace(/LucideIcons\.Routes\b/g, 'LucideIcons.Route')
  //     .replace(/LucideIcons\.Dot\b/g, 'LucideIcons.Circle');
  
  //   // 5️⃣ Fix JSX expression spacing issues
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /(\{[^}]+\})\s+(<(?:br|hr|img)[^>]*\/?>)/gi,
  //     '$1{" "}$2'
  //   );
    
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /(\{[^}]+\})\s+(<(?!\/)[a-z][^>]*>)/gi,
  //     (match, expr, tag) => {
  //       if (!match.includes('{" "}')) {
  //         return `${expr}{" "}${tag}`;
  //       }
  //       return match;
  //     }
  //   );
    
  //   // 6️⃣ Fix common chart component errors
  //   cleanedTemplate = cleanedTemplate
  //     .replace(/new\s+Chart\(/g, '') // Remove "new Chart(" - use components directly
  //     .replace(/Chart\.register\(/g, ''); // Remove Chart.register calls
    
  //   // 7️⃣ Fix template literal issues in className
  //   // Fix cases where template literals might have unclosed backticks or issues
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /className=\{\`([^`]*)\`\}/g,
  //     (match, content) => {
  //       // Ensure backticks are properly escaped if needed
  //       const fixed = content.replace(/`/g, '\\`');
  //       return `className={\`${fixed}\`}`;
  //     }
  //   );
    
  //   // 8️⃣ Fix potential issues with style tags and template literals
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /<style>\s*\{`([^`]*)`\}\s*<\/style>/g,
  //     (match, content) => {
  //       return `<style>{\`${content}\`}</style>`;
  //     }
  //   );
    
  //   // 9️⃣ Remove any invisible/special characters that might cause parsing issues
  //   cleanedTemplate = cleanedTemplate
  //     .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
  //     .replace(/\u00A0/g, ' '); // Replace non-breaking spaces with regular spaces
    
  //   // 🔟 Fix potential issues with JSX comments that might break parsing
  //   cleanedTemplate = cleanedTemplate.replace(
  //     /\/\*([^*]|\*[^/])*\*\//g,
  //     (match) => {
  //       // Ensure comments don't break JSX parsing
  //       return match.replace(/\n/g, ' ');
  //     }
  //   );
    
  //   // 1️⃣1️⃣ Ensure the function is properly closed
  //   // Count opening and closing braces to ensure the function is complete
  //   const openBraces = (cleanedTemplate.match(/\{/g) || []).length;
  //   const closeBraces = (cleanedTemplate.match(/\}/g) || []).length;
  //   if (openBraces > closeBraces) {
  //     console.warn('⚠️ Template has unmatched braces, attempting to fix...');
  //     cleanedTemplate = cleanedTemplate + '}'.repeat(openBraces - closeBraces);
  //   }
    
  //   // 1️⃣2️⃣ Ensure the component function ends properly
  //   // If the template doesn't end with a closing brace and semicolon, add it
  //   const trimmed = cleanedTemplate.trim();
  //   if (!trimmed.endsWith('}') && !trimmed.endsWith('};')) {
  //     // Check if it ends with a return statement that might be incomplete
  //     if (trimmed.match(/return\s*\(/)) {
  //       // Try to find if there are unclosed parentheses
  //       const openParens = (trimmed.match(/\(/g) || []).length;
  //       const closeParens = (trimmed.match(/\)/g) || []).length;
  //       if (openParens > closeParens) {
  //         cleanedTemplate = cleanedTemplate + ')'.repeat(openParens - closeParens);
  //       }
  //       // Ensure the function closes
  //       if (!trimmed.endsWith('}')) {
  //         cleanedTemplate = cleanedTemplate + '\n}';
  //       }
  //     }
  //   }
  
  //   const newCode = `
  // ${cleanedTemplate}
  
  // render(<${componentName} data={data} />);
  //   `.trim();
  
  //     // Log the cleaned template in development for debugging
  //     if (process.env.NODE_ENV === 'development') {
  //       console.log('🧹 Cleaned template preview (first 500 chars):', newCode.substring(0, 500));
  //     }
    
  //     setCleanTemplate(newCode);
  //   } catch (error) {
  //     console.error('❌ Error cleaning template:', error);
  //     console.error('Template preview (first 1000 chars):', template.substring(0, 1000));
  //     // Set a minimal error template that will show the error
  //     setCleanTemplate(`
  //       function ErrorComponent() {
  //         return React.createElement('div', { 
  //           style: { padding: '20px', color: 'red', border: '1px solid red' },
  //           children: 'Template processing error: ' + ${JSON.stringify(error.message)}
  //         });
  //       }
  //       render(<ErrorComponent />);
  //     `.trim());
  //   }
  // }, [template, componentName]);


  useEffect(() => {
    if (!template) {
      setCleanTemplate('');
      return;
    }
  
    try {
      let cleanedTemplate = template
        .replace(/import\s+.*?from\s+['"].*?['"];?\n?/g, '')
        .replace(/export default\s+/g, "")
        // ✅ ADD THIS LINE - Remove window.LucideIcons
        .replace(/const\s+LucideIcons\s*=\s*window\.LucideIcons\s*\|\|\s*\{\};?\n?/g, '')
        .replace(/\\`/g, '`')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\u00A0/g, ' ')
        .trim();
  
      const newCode = `
  ${cleanedTemplate}
  
  render(<${componentName} data={data} />);
      `.trim();
  
      setCleanTemplate(newCode);
    } catch (error) {
      console.error('❌ Error processing template:', error);
      setCleanTemplate(`
        function ErrorComponent() {
          return React.createElement('div', { 
            style: { padding: '20px', color: 'red', border: '1px solid red' },
            children: 'Template processing error: ' + ${JSON.stringify(error.message)}
          });
        }
        render(<ErrorComponent />);
      `.trim());
    }
  }, [template, componentName]);




 const fontFamilyHelper = getFontFamily(data);

  // Complete scope with ALL chart types
  const scope = useMemo(() => {
    // Ensure React is available before creating scope
    if (!React) {
      console.error('React is not available for LiveProvider scope');
      return {};
    }
    

    return {
      data,
      React,
      // All Chart Components
      Pie,
      Bar,
      Line,
      Doughnut,
      Radar,
      PolarArea,
      Bubble,
      Scatter,
      ChartJS,
      // Lucide Icons namespace object
      LucideIcons,
      // Lucide as alias for LucideIcons (for template compatibility)
      Lucide: LucideIcons,
      // All Lucide Icons (spread for individual access)
      ...LucideIcons,
      // Icon aliases for common variations
      LightningBolt: LucideIcons.Zap,
      Lightning: LucideIcons.Zap,
      CheckCircle: LucideIcons.CheckCircle,
      PieChart: LucideIcons.PieChart,

    CheckCircle2: LucideIcons.CheckCircle2 || LucideIcons.CheckCircle, // ADD THIS LINE
    PieChart: LucideIcons.PieChart,
    BarChart3: LucideIcons.BarChart3 || LucideIcons.BarChart, // ADD THIS LINE
      // A11y is commonly used shorthand for Accessibility
      A11y: LucideIcons.Accessibility || LucideIcons.Info,
      // Common incorrect icon names that LLMs generate - provide fallbacks
      Routes: LucideIcons.Route,
      Dot: LucideIcons.Circle,
      // Common icon fallbacks used in templates
      MonitorSmartphone: LucideIcons.MonitorSmartphone || LucideIcons.Monitor || LucideIcons.Smartphone,
      Quote: LucideIcons.Quote || LucideIcons.MessageSquare,
      // Font helper functions
      getFontFamily: () => fontFamilyHelper,
      defaultFontFamily: fontFamilyHelper,
      // Animation library - Framer Motion
      motion,
      AnimatePresence,
      // Explicit React Hooks for safety in evaluated code
      useState: React.useState,
      useEffect: React.useEffect,
      useContext: React.useContext,
      useRef: React.useRef,
      useMemo: React.useMemo,
      useCallback: React.useCallback,
      // Also provide createElement for dynamic icon rendering
      createElement: React.createElement,
    };
  }, [data, fontFamilyHelper]);

  const previewContent = (
    <>
      {withContainer && (
        <div className="mb-4 flex items-center justify-between no-print">
          <h1 className="text-2xl font-bold text-gray-800">Collateral Preview</h1>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Live</span>
        </div>
      )}
      <div 
        ref={previewContainerRef}
        data-preview-container
        className="relative"
      >
        <LiveProvider 
          code={cleanTemplate} 
          scope={scope} 
          noInline={true}
        >
          {/* Hidden LiveError for error detection - invisible but functional */}
          <div 
            ref={errorRef} 
            className="sr-only"
            style={{ visibility: 'hidden', position: 'absolute', width: '1px', height: '1px', overflow: 'hidden' }}
            aria-hidden="true"
          >
            <LiveError />
          </div>
          {/* Custom error display with CTA */}
          <PreviewError 
            error={previewError} 
            onReportError={onErrorReport}
          />
          <LivePreview />
        </LiveProvider>
      </div>
      {/* Floating Chat Input */}
      {floatingChat && (
        <FloatingChatInput
          x={floatingChat.x}
          y={floatingChat.y}
          onSend={handleFloatingChatSend}
          onClose={handleFloatingChatClose}
          defaultMessage={floatingChat.defaultMessage}
        />
      )}
    </>
  );

  if (withContainer) {
    return (
      <div className="flex-[3] overflow-y-auto p-6 bg-white border-l border-gray-200">
        <div className="max-w-5xl mx-auto">
          {previewContent}
        </div>
      </div>
    );
  }

  return previewContent;
}

export default React.memo(CollateralPreview);
